import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useI18n } from '../context/I18nContext';
import './CharacterManagement.css';

function CharacterManagement({ initialSection = 'ped', showInternalNav = true }) {
  const { hasPermission, apiFetch } = useAuth();
  const { language } = useI18n();
  const [identifier, setIdentifier] = useState('');
  const [pedModel, setPedModel] = useState('');
  const [result, setResult] = useState(null);
  const [moveFromLicense, setMoveFromLicense] = useState('');
  const [moveToLicense, setMoveToLicense] = useState('');
  const [duplicateStrategy, setDuplicateStrategy] = useState('skip');
  const [movePreview, setMovePreview] = useState(null);
  const [moveResult, setMoveResult] = useState(null);
  const [movePreviewLoading, setMovePreviewLoading] = useState(false);
  const [moveExecuteLoading, setMoveExecuteLoading] = useState(false);
  const [deleteIdentifier, setDeleteIdentifier] = useState('');
  const [deletePreview, setDeletePreview] = useState(null);
  const [deleteResult, setDeleteResult] = useState(null);
  const [deletePreviewLoading, setDeletePreviewLoading] = useState(false);
  const [deleteExecuteLoading, setDeleteExecuteLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeSection, setActiveSection] = useState('ped');

  const t = language === 'da'
    ? {
        failedSetPed: 'Kunne ikke sætte ped model',
        failedPreview: 'Kunne ikke forhåndsvise karakterflyt',
        failedExecute: 'Kunne ikke udføre karakterflyt',
        failedDeletePreview: 'Kunne ikke forhåndsvise karaktersletning',
        failedDeleteExecute: 'Kunne ikke slette karakter',
        title: 'Karakteradministration',
        subtitle: 'sæt eller ændr spilleres ped modeller',
        quickSelect: 'Hurtigvalg',
        playerIdentifier: 'Spiller identifikator',
        pedModel: 'Ped model',
        pedPlaceholder: 'Indtast ped model (fx mp_m_freemode_01)',
        pedHint: 'Brug hurtigvalg ovenfor eller indtast et brugerdefineret modelnavn eller hash (fx 1885233650)',
        settingPed: 'sætter ped...',
        setPed: 'sæt ped model',
        moveTitle: 'Karakterflyt',
        moveDescription: 'overfører identifikatorer fra en license til en anden pa tværs af databasetabeller.',
        fromLicense: 'Fra license',
        toLicense: 'Til license',
        duplicateStrategy: 'Duplicate strategi',
        skipDuplicates: 'Spring duplicates over',
        overrideDuplicates: 'Overskriv duplicates',
        previewing: 'forhåndsviser...',
        previewTransfer: 'forhåndsvis overførsel',
        transferPreview: 'overførselsforhåndsvisning',
        totalRecords: 'Samlede records',
        tablesAffected: 'Paavirkede tabeller',
        charactersFound: 'Fundne karakterer',
        charactersSample: 'Karakterer (eksempel)',
        tablesSample: 'Tabeller (eksempel)',
        executing: 'udfører overførsel...',
        executeTransfer: 'udfør overførsel',
        moveComplete: 'Karakterflyt gennemført',
        recordsUpdated: 'Opdaterede records',
        tablesUpdated: 'Opdaterede tabeller',
        charactersMoved: 'Flyttede karakterer',
        duplicatesSkipped: 'Oversprungne duplicates',
        duplicatesOverridden: 'Overskrevne duplicates',
        deleteTitle: 'Komplet karaktersletning',
        deleteDescription: 'Sletter hele karakteren og alle records hvor karakteren er ejer/identifier i databasen.',
        deleteIdentifierLabel: 'Karakter identifier',
        deleteIdentifierPlaceholder: 'fx char1:xxxxxxxx',
        previewDelete: 'forhåndsvis sletning',
        deleting: 'sletter karakter...',
        executeDelete: 'udfør komplet sletning',
        deletePreviewTitle: 'Sletningsforhåndsvisning',
        deleteResultTitle: 'Karakter slettet',
        totalDeleteRows: 'Rows til sletning',
        deleteTablesAffected: 'Tabeller med matches',
        deletedRows: 'Slettede rows',
        failedTables: 'Fejlede tabeller',
        matchedTables: 'Matchede tabeller',
        deletedTables: 'Slettede tabeller',
        deleteConfirmTitle: 'Bekræft sletning',
        deleteConfirmText: 'Skriv DELETE for at fortsætte. Denne handling kan ikke fortrydes.',
        cancelDelete: 'Sletning annulleret',
        error: 'Fejl',
        pedUpdated: 'Ped model opdateret',
        player: 'Spiller:',
        previousModel: 'Tidligere model:',
        newModel: 'Ny model:',
        modelHash: 'Model Hash (Database):',
        playerOnline: 'Spiller online:',
        skinReloaded: 'Skin genindlæst:',
        yes: 'Ja',
        no: 'Nej',
        liveUpdate: 'Ændringen blev anvendt med det samme!',
        offlineUpdate: 'Ændringen kræver at spilleren relogger.',
        note: 'Note:',
        relogNote: 'Spilleren skal muligvis relogge for at ændringen træder i kraft.',
        help: 'Hjaelp',
        warning: 'Advarsel:',
        warningText: 'Ugyldige ped modeller kan goere spilleren usynlig eller crashe spillet.',
        whatIsPed: 'Hvad er en ped model?',
        pedExplanation: 'En ped model er karakterens skin/model in-game. Du kan ændre den manuelt her.',
        commonFormats: 'Almindelige model-formater:',
        navPed: 'Sæt Ped Model',
        navMove: 'Flyt Karakter',
        navDelete: 'Slet Karakter'
      }
    : {
        failedSetPed: 'Failed to set ped model',
        failedPreview: 'Failed to preview character move',
        failedExecute: 'Failed to execute character move',
        failedDeletePreview: 'Failed to preview character deletion',
        failedDeleteExecute: 'Failed to delete character',
        title: 'Character Management',
        subtitle: 'Set or change player ped models',
        quickSelect: 'Quick Select',
        playerIdentifier: 'Player Identifier',
        pedModel: 'Ped Model',
        pedPlaceholder: 'Enter ped model (e.g., mp_m_freemode_01)',
        pedHint: 'Use Quick Select above or enter a custom model name or hash (e.g., 1885233650)',
        settingPed: 'Setting Ped...',
        setPed: 'Set Ped Model',
        moveTitle: 'Character Move',
        moveDescription: 'Transfers identifiers from one license to another across database tables.',
        fromLicense: 'From License',
        toLicense: 'To License',
        duplicateStrategy: 'Duplicate Strategy',
        skipDuplicates: 'Skip duplicates',
        overrideDuplicates: 'Override duplicates',
        previewing: 'Previewing...',
        previewTransfer: 'Preview Transfer',
        transferPreview: 'Transfer Preview',
        totalRecords: 'Total Records',
        tablesAffected: 'Tables Affected',
        charactersFound: 'Characters Found',
        charactersSample: 'Characters (sample)',
        tablesSample: 'Tables (sample)',
        executing: 'Executing Transfer...',
        executeTransfer: 'Execute Transfer',
        moveComplete: 'Character Move Complete',
        recordsUpdated: 'Records Updated',
        tablesUpdated: 'Tables Updated',
        charactersMoved: 'Characters Moved',
        duplicatesSkipped: 'Duplicates Skipped',
        duplicatesOverridden: 'Duplicates Overridden',
        deleteTitle: 'Full Character Deletion',
        deleteDescription: 'Deletes the character and all rows where that character is the owner/identifier across the database.',
        deleteIdentifierLabel: 'Character Identifier',
        deleteIdentifierPlaceholder: 'e.g. char1:xxxxxxxx',
        previewDelete: 'Preview Deletion',
        deleting: 'Deleting Character...',
        executeDelete: 'Execute Full Deletion',
        deletePreviewTitle: 'Deletion Preview',
        deleteResultTitle: 'Character Deleted',
        totalDeleteRows: 'Rows To Delete',
        deleteTablesAffected: 'Tables With Matches',
        deletedRows: 'Rows Deleted',
        failedTables: 'Failed Tables',
        matchedTables: 'Matched Tables',
        deletedTables: 'Deleted Tables',
        deleteConfirmTitle: 'Confirm deletion',
        deleteConfirmText: 'Type DELETE to continue. This action cannot be undone.',
        cancelDelete: 'Deletion cancelled',
        error: 'Error',
        pedUpdated: 'Ped Model Updated Successfully',
        player: 'Player:',
        previousModel: 'Previous Model:',
        newModel: 'New Model:',
        modelHash: 'Model Hash (Database):',
        playerOnline: 'Player Online:',
        skinReloaded: 'Skin Reloaded:',
        yes: 'Yes',
        no: 'No',
        liveUpdate: 'Changes applied instantly!',
        offlineUpdate: 'Changes require player to relog.',
        note: 'Note:',
        relogNote: 'Player may need to relog for changes to take effect.',
        help: 'Help',
        warning: 'Warning:',
        warningText: 'Invalid ped models may cause the player to appear invisible or crash their game.',
        whatIsPed: 'What is a ped model?',
        pedExplanation: 'A ped model is the character skin/model used in-game. You can change it here manually.',
        commonFormats: 'Common Model Formats:',
        navPed: 'Set Ped Model',
        navMove: 'Move Character',
        navDelete: 'Delete Character'
      };

  const commonPeds = [
    { name: 'Michael', model: 'mp_m_freemode_01' },
    { name: 'Franklin', model: 'mp_f_freemode_01' },
    { name: 'Trevor', model: 'a_m_y_hipster_01' },
    { name: 'Business Man', model: 'a_m_m_business_01' },
    { name: 'Beach Girl', model: 'a_f_y_beach_01' },
    { name: 'Cop', model: 's_m_y_cop_01' },
    { name: 'Paramedic', model: 's_m_m_paramedic_01' },
    { name: 'Firefighter', model: 's_m_y_fireman_01' },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const response = await apiFetch('/api/character/setped', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ identifier, model: pedModel })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || t.failedSetPed);
      }

      const data = await response.json();
      setResult(data);
      
      // Reset form
      setIdentifier('');
      setPedModel('');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const selectPed = (model) => {
    setPedModel(model);
  };

  const handleSectionChange = (section) => {
    setActiveSection(section);
    setError(null);
  };

  useEffect(() => {
    const nextSection = String(initialSection || '').trim().toLowerCase();
    if (nextSection === 'ped' || nextSection === 'move' || nextSection === 'delete') {
      setActiveSection(nextSection);
    }
  }, [initialSection]);

  const handlePreviewCharacterMove = async (e) => {
    e.preventDefault();
    setError(null);
    setMoveResult(null);
    setMovePreview(null);
    setMovePreviewLoading(true);

    try {
      const response = await apiFetch('/api/character/move/preview', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fromLicense: moveFromLicense.trim(), toLicense: moveToLicense.trim() })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || t.failedPreview);
      }

      const data = await response.json();
      setMovePreview(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setMovePreviewLoading(false);
    }
  };

  const handleExecuteCharacterMove = async () => {
    setError(null);
    setMoveResult(null);
    setMoveExecuteLoading(true);

    try {
      const response = await apiFetch('/api/character/move/execute', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fromLicense: moveFromLicense.trim(), toLicense: moveToLicense.trim(), duplicateStrategy })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || t.failedExecute);
      }

      const data = await response.json();
      setMoveResult(data);
      setMovePreview(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setMoveExecuteLoading(false);
    }
  };

  const handlePreviewCharacterDelete = async (e) => {
    e.preventDefault();
    setError(null);
    setDeleteResult(null);
    setDeletePreview(null);
    setDeletePreviewLoading(true);

    try {
      const response = await apiFetch('/api/character/delete/preview', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ identifier: deleteIdentifier.trim() })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || t.failedDeletePreview);
      }

      const data = await response.json();
      setDeletePreview(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setDeletePreviewLoading(false);
    }
  };

  const handleExecuteCharacterDelete = async () => {
    setError(null);
    setDeleteResult(null);

    const confirmation = window.prompt(`${t.deleteConfirmTitle}\n${t.deleteConfirmText}`, '');
    if (confirmation !== 'DELETE') {
      setError(t.cancelDelete);
      return;
    }

    setDeleteExecuteLoading(true);
    try {
      const response = await apiFetch('/api/character/delete/execute', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ identifier: deleteIdentifier.trim(), confirmation: 'DELETE' })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || t.failedDeleteExecute);
      }

      const data = await response.json();
      setDeleteResult(data);
      setDeletePreview(null);
      setDeleteIdentifier('');
    } catch (err) {
      setError(err.message);
    } finally {
      setDeleteExecuteLoading(false);
    }
  };

  return (
    <div className="character-management">
      <div className="character-header">
        <h2>{t.title}</h2>
      </div>

      {showInternalNav && (
        <div className="character-nav">
          {hasPermission('characters.edit') && (
            <button
              className={activeSection === 'ped' ? 'active' : ''}
              onClick={() => handleSectionChange('ped')}
            >
              {t.navPed}
            </button>
          )}
          {hasPermission('characters.move') && (
            <button
              className={activeSection === 'move' ? 'active' : ''}
              onClick={() => handleSectionChange('move')}
            >
              {t.navMove}
            </button>
          )}
          {hasPermission('characters.delete') && (
            <button
              className={activeSection === 'delete' ? 'active' : ''}
              onClick={() => handleSectionChange('delete')}
            >
              {t.navDelete}
            </button>
          )}
        </div>
      )}

      {activeSection === 'ped' && hasPermission('characters.edit') && (
        <>
        <div className="common-peds">
          <h3>{t.quickSelect}</h3>
        <div className="peds-grid">
          {commonPeds.map((ped, index) => (
            <button
              key={index}
              className={`ped-button ${pedModel === ped.model ? 'selected' : ''}`}
              onClick={() => selectPed(ped.model)}
              type="button"
            >
              <span className="ped-name">{ped.name}</span>
              <span className="ped-model">{ped.model}</span>
            </button>
          ))}
        </div>
      </div>

      <form onSubmit={handleSubmit} className="character-form">
        <div className="form-group">
          <label>{t.playerIdentifier}</label>
          <input
            type="text"
            placeholder="char1:xxxxx or license:xxxxx"
            value={identifier}
            onChange={(e) => setIdentifier(e.target.value)}
            required
          />
        </div>

        <div className="form-group">
          <label>{t.pedModel}</label>
          <input
            type="text"
            placeholder={t.pedPlaceholder}
            value={pedModel}
            onChange={(e) => setPedModel(e.target.value)}
            required
          />
          <small>{t.pedHint}</small>
        </div>

        <button type="submit" className="submit-button" disabled={loading}>
          {loading ? t.settingPed : `${t.setPed}`}
        </button>
      </form>

      {error && (
        <div className="alert error">
          <strong>{t.error}:</strong> {error}
        </div>
      )}

      {result && (
        <div className="alert success">
          <h3>{t.pedUpdated}</h3>
          <div className="result-details">
            <div className="detail-row">
              <span>{t.player}</span>
              <strong>{result.identifier}</strong>
            </div>
            <div className="detail-row">
              <span>{t.previousModel}</span>
              <strong>{result.previousModel}</strong>
            </div>
            <div className="detail-row">
              <span>{t.newModel}</span>
              <strong className="highlight">{result.newModel}</strong>
            </div>
            {result.modelHash && (
              <div className="detail-row">
                <span>{t.modelHash}</span>
                <strong className="code-text">{result.modelHash}</strong>
              </div>
            )}
            <div className="detail-row">
              <span>{t.playerOnline}</span>
              <strong className={result.online ? 'success-text' : 'warning-text'}>
                {result.online ? `${t.yes}` : `${t.no}`}
              </strong>
            </div>
            {result.online && (
              <div className="detail-row">
                <span>{t.skinReloaded}</span>
                <strong className={result.reloadTriggered ? 'success-text' : 'warning-text'}>
                  {result.reloadTriggered ? `${t.yes}` : `${t.no}`}
                </strong>
              </div>
            )}
          </div>
          <div className={result.online && result.reloadTriggered ? 'info-note success-bg' : 'info-note'}>
            <strong>{t.note}</strong> {result.online && result.reloadTriggered ? t.liveUpdate : t.offlineUpdate}
          </div>
        </div>
      )}

      <div className="help-section">
        <h3>{t.help}</h3>
        <div className="help-content">
          <p><strong>{t.whatIsPed}</strong></p>
          <p>{t.pedExplanation}</p>
          
          <p><strong>{t.commonFormats}</strong></p>
          <ul>
            <li><code>mp_m_freemode_01</code> - Male character</li>
            <li><code>mp_f_freemode_01</code> - Female character</li>
            <li><code>a_m_y_*</code> - Male NPC models</li>
            <li><code>a_f_y_*</code> - Female NPC models</li>
            <li><code>s_m_y_*</code> - Special male models (cops, etc.)</li>
          </ul>

          <p><strong>{t.warning}</strong> {t.warningText}</p>
        </div>
      </div>
        </>
      )}

      {activeSection === 'move' && hasPermission('characters.move') && (
      <div className="common-peds move-section">
        <h3>{t.moveTitle}</h3>
        <p className="move-description">
          {t.moveDescription}
        </p>

        <form onSubmit={handlePreviewCharacterMove} className="character-form move-form">
          <div className="form-group">
            <label>{t.fromLicense}</label>
            <input
              type="text"
              placeholder="e.g. license:oldvalue"
              value={moveFromLicense}
              onChange={(e) => setMoveFromLicense(e.target.value)}
              required
            />
          </div>

          <div className="form-group">
            <label>{t.toLicense}</label>
            <input
              type="text"
              placeholder="e.g. license:newvalue"
              value={moveToLicense}
              onChange={(e) => setMoveToLicense(e.target.value)}
              required
            />
          </div>

          <div className="form-group">
            <label>{t.duplicateStrategy}</label>
            <select
              className="strategy-select"
              value={duplicateStrategy}
              onChange={(e) => setDuplicateStrategy(e.target.value)}
            >
              <option value="skip">{t.skipDuplicates}</option>
              <option value="override">{t.overrideDuplicates}</option>
            </select>
          </div>

          <button type="submit" className="submit-button" disabled={movePreviewLoading || moveExecuteLoading}>
            {movePreviewLoading ? t.previewing : `${t.previewTransfer}`}
          </button>
        </form>

        {movePreview && (
          <div className="alert success">
            <h3>{t.transferPreview}</h3>
            <div className="result-details">
              <div className="detail-row">
                <span>{t.totalRecords}</span>
                <strong>{movePreview.summary.totalRecordsAffected}</strong>
              </div>
              <div className="detail-row">
                <span>{t.tablesAffected}</span>
                <strong>{movePreview.summary.totalTablesAffected}</strong>
              </div>
              <div className="detail-row">
                <span>{t.charactersFound}</span>
                <strong>{movePreview.summary.totalCharactersFound}</strong>
              </div>
            </div>

            <div className="preview-lists">
              <div>
                <h4>{t.charactersSample}</h4>
                <ul>
                  {movePreview.characters.slice(0, 5).map((char, index) => (
                    <li key={`${char.oldIdentifier}-${index}`}>
                      {char.name}: {char.oldIdentifier} → {char.newIdentifier}
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h4>{t.tablesSample}</h4>
                <ul>
                  {movePreview.tablesAffected.slice(0, 10).map((entry, index) => (
                    <li key={`${entry.table}-${entry.column}-${index}`}>
                      {entry.table}.{entry.column} ({entry.count})
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <button
              type="button"
              className="submit-button move-execute-button"
              onClick={handleExecuteCharacterMove}
              disabled={moveExecuteLoading}
            >
              {moveExecuteLoading ? t.executing : `${t.executeTransfer} (${duplicateStrategy})`}
            </button>
          </div>
        )}

        {moveResult && (
          <div className="alert success">
            <h3>{t.moveComplete}</h3>
            <div className="result-details">
              <div className="detail-row">
                <span>{t.recordsUpdated}</span>
                <strong>{moveResult.summary.recordsUpdated}</strong>
              </div>
              <div className="detail-row">
                <span>{t.tablesUpdated}</span>
                <strong>{moveResult.summary.tablesAffected}</strong>
              </div>
              <div className="detail-row">
                <span>{t.charactersMoved}</span>
                <strong>{moveResult.summary.charactersMoved}</strong>
              </div>
              <div className="detail-row">
                <span>{t.duplicatesSkipped}</span>
                <strong>{moveResult.summary.duplicatesSkipped}</strong>
              </div>
              <div className="detail-row">
                <span>{t.duplicatesOverridden}</span>
                <strong>{moveResult.summary.duplicatesOverridden}</strong>
              </div>
            </div>
          </div>
        )}

        {error && (
          <div className="alert error">
            <strong>{t.error}:</strong> {error}
          </div>
        )}
      </div>
      )}

      {activeSection === 'delete' && hasPermission('characters.delete') && (
      <div className="common-peds delete-section">
        <h3>{t.deleteTitle}</h3>
        <p className="move-description">{t.deleteDescription}</p>

        <form onSubmit={handlePreviewCharacterDelete} className="character-form move-form">
          <div className="form-group">
            <label>{t.deleteIdentifierLabel}</label>
            <input
              type="text"
              placeholder={t.deleteIdentifierPlaceholder}
              value={deleteIdentifier}
              onChange={(e) => setDeleteIdentifier(e.target.value)}
              required
            />
          </div>

          <button type="submit" className="submit-button" disabled={deletePreviewLoading || deleteExecuteLoading}>
            {deletePreviewLoading ? t.previewing : `${t.previewDelete}`}
          </button>
        </form>

        {deletePreview && (
          <div className="alert error">
            <h3>{t.deletePreviewTitle}</h3>
            <div className="result-details">
              <div className="detail-row">
                <span>{t.player}</span>
                <strong>{deletePreview.character?.name || deletePreview.identifier}</strong>
              </div>
              <div className="detail-row">
                <span>{t.totalDeleteRows}</span>
                <strong>{deletePreview.summary.totalRows}</strong>
              </div>
              <div className="detail-row">
                <span>{t.deleteTablesAffected}</span>
                <strong>{deletePreview.summary.tablesAffected}</strong>
              </div>
            </div>

            <div className="preview-lists">
              <div>
                <h4>{t.matchedTables}</h4>
                <ul>
                  {deletePreview.targets.slice(0, 20).map((entry, index) => (
                    <li key={`${entry.table}-${entry.column}-${index}`}>
                      {entry.table}.{entry.column} ({entry.count})
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <button
              type="button"
              className="submit-button delete-execute-button"
              onClick={handleExecuteCharacterDelete}
              disabled={deleteExecuteLoading}
            >
              {deleteExecuteLoading ? t.deleting : `${t.executeDelete}`}
            </button>
          </div>
        )}

        {deleteResult && (
          <div className="alert success">
            <h3>{t.deleteResultTitle}</h3>
            <div className="result-details">
              <div className="detail-row">
                <span>{t.player}</span>
                <strong>{deleteResult.character?.name || deleteResult.identifier}</strong>
              </div>
              <div className="detail-row">
                <span>{t.deletedRows}</span>
                <strong>{deleteResult.summary.rowsDeleted}</strong>
              </div>
              <div className="detail-row">
                <span>{t.deleteTablesAffected}</span>
                <strong>{deleteResult.summary.tablesAffected}</strong>
              </div>
              <div className="detail-row">
                <span>{t.failedTables}</span>
                <strong>{deleteResult.summary.failedTables}</strong>
              </div>
            </div>

            {Array.isArray(deleteResult.deletedEntries) && deleteResult.deletedEntries.length > 0 && (
              <div className="preview-lists">
                <div>
                  <h4>{t.deletedTables}</h4>
                  <ul>
                    {deleteResult.deletedEntries.slice(0, 20).map((entry, index) => (
                      <li key={`${entry.table}-${entry.column}-${index}`}>
                        {entry.table}.{entry.column} ({entry.deletedRows})
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
          </div>
        )}

        {error && (
          <div className="alert error">
            <strong>{t.error}:</strong> {error}
          </div>
        )}
      </div>
      )}

    </div>
  );
}

export default CharacterManagement;



